import { useQuery } from "@tanstack/react-query";
import { getChatApiClient } from "@shared/apis/sidekick/api-factories";
import { GetThreadSuggestedPromptsResponse } from "@shared/apis/sidekick/generated";
import { extractQueryState } from "@shared/utils/extractQueryState";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { appLogger } from "@shared/services/app-logger";

type Props = {
  onSuccess?: (data: GetThreadSuggestedPromptsResponse) => void;
  threadId: string;
  exceptions?: string[];
  enabled: boolean;
};

const GENERATE_THREAD_PROMPT_SUGGESTIONS_QUERY_KEY =
  "generate-thread-prompt-suggestions";

const buildGenerateThreadSuggestionPromptKey = ({
  threadId,
  exceptions,
}: {
  threadId: string | null;
  exceptions?: string[];
}) => {
  return [
    GENERATE_THREAD_PROMPT_SUGGESTIONS_QUERY_KEY,
    threadId,
    exceptions ?? [],
  ];
};

export const useGenerateThreadSuggestionPrompt = (props: Props) => {
  const { onSuccess, threadId, exceptions, enabled } = props;
  const chatApi = getChatApiClient();

  const query = useQuery({
    queryKey: buildGenerateThreadSuggestionPromptKey({ threadId, exceptions }),
    queryFn: async ({ signal }) => {
      try {
        const response =
          await chatApi.chatControllerGenerateThreadSuggestedPrompts(
            {
              thread: threadId ?? "",
              getThreadSuggestedPromptsRequest: {
                exceptions: exceptions,
              },
            },
            { signal },
          );
        onSuccess?.(response);
        appEventEmitter.emit({
          eventType: "sidekick-suggestions-request-success",
        });
        return response;
      } catch (error) {
        appEventEmitter.emit({
          eventType: "sidekick-suggestions-request-failure",
        });
        appLogger.error("Failed to get generate proactive suggested prompts.", {
          error,
        });
        throw error;
      }
    },
    enabled: !!threadId && enabled,
    retry: 1,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 30,
    staleTime: Infinity,
  });

  return extractQueryState(query);
};
